/* eslint-disable camelcase */
import { useState } from 'react';
import { alpha, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { GetInvoiceMissionTypeDescription, InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';

// Läsbart datum ur aviseringens datumsträng (YYYYMMDD eller YYYY-MM-DD).
const missionDate = (s?: string): string => {
  const t = (s ?? '').trim();
  if (/^\d{8}$/.test(t)) return dayjs(`${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`).format('YYYY-MM-DD');
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return t;
};

interface SendingPreviousMissionPickerProps {
  missions: InvoiceMissionResponse[];
  selectedId: number | null;
  onSelect: (mission: InvoiceMissionResponse) => void;
  emptyText?: string;
  // Text som visas under listan för att förklara att bara ett urval hämtas (t.ex. "Visar de 10 senaste aviseringarna").
  limitNote?: string;
  // Visas medan aviseringslistan läses in (kan ta några sekunder för kunder med mycket data).
  loading?: boolean;
}

// Väljare av tidigare avisering som en tabell (radioknapp, namn, typ, datum) i stället för en droplista. När en rad
// valts fälls listan ihop till den valda raden – "Byt avisering" fäller ut hela listan igen.
export const SendingPreviousMissionPicker = ({
  missions,
  selectedId,
  onSelect,
  emptyText,
  limitNote,
  loading = false,
}: SendingPreviousMissionPickerProps) => {
  const { t } = useTranslation(['invoice']);
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 5, color: 'text.secondary' }}>
        <CircularProgress size={22} />
        <Typography variant='body2'>Läser in aviseringar …</Typography>
      </Box>
    );
  }

  if (missions.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 3, textAlign: 'center' }}>
        {emptyText ?? 'Inga tidigare aviseringar'}
      </Typography>
    );
  }

  const selectedMission = selectedId != null ? missions.find((m) => m.invoiceMission_id === selectedId) ?? null : null;
  // Vald rad + inte utfälld → visa bara den valda raden. Annars hela listan.
  const collapsed = !!selectedMission && !expanded;
  const rows = collapsed && selectedMission ? [selectedMission] : missions;

  const handleSelect = (mission: InvoiceMissionResponse) => {
    onSelect(mission);
    setExpanded(false);
  };

  return (
    <>
      <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell sx={{ fontWeight: 700 }}>Namn</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Datum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => {
              const selected = m.invoiceMission_id === selectedId;
              return (
                <TableRow
                  key={m.invoiceMission_id}
                  hover
                  selected={selected}
                  onClick={() => handleSelect(m)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: (th) => alpha(th.palette.primary.main, 0.12) },
                  }}
                >
                  <TableCell padding='checkbox'>
                    {selected ? (
                      <RadioButtonChecked fontSize='small' color='primary' />
                    ) : (
                      <RadioButtonUnchecked fontSize='small' sx={{ color: 'text.disabled' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: selected ? 700 : 400 }}>{m.missionName}</TableCell>
                  <TableCell>{GetInvoiceMissionTypeDescription((m.type as keyof typeof InvoiceMissionTypeTextEnum) ?? 'unknown', t)}</TableCell>
                  <TableCell>{missionDate(m.date)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1, minHeight: 30 }}>
        {!collapsed && limitNote ? (
          <Typography variant='caption' color='text.secondary'>
            {limitNote}
          </Typography>
        ) : (
          <span />
        )}
        {collapsed && (
          <Button size='small' variant='text' onClick={() => setExpanded(true)} sx={{ textTransform: 'none' }}>
            Byt avisering
          </Button>
        )}
      </Box>
    </>
  );
};

export default SendingPreviousMissionPicker;
